import React, { useEffect, useState } from 'react';
import { FunnelLayout } from '../../components/FunnelLayout';
import { SoWhatCallout } from '../../components/funnel/SoWhatCallout';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { ActivityLevel } from '../../types';

interface ActivityOption {
  key: ActivityLevel;
  labelKey: string;
  label: string;
  rangeKey: string;
  range: string;
  imageKey: string;
  imageDefault: string;
}

const ACTIVITY_OPTIONS: ActivityOption[] = [
  {
    key: ActivityLevel.BAJO,
    labelKey: 'p5.activity_bajo_label',
    label: 'Tranquilo',
    rangeKey: 'p5.activity_bajo_range',
    range: '< 30 min/día',
    imageKey: 'p5.image.bajo',
    imageDefault: '/images/funnel/activity-bajo.png',
  },
  {
    key: ActivityLevel.MEDIO,
    labelKey: 'p5.activity_medio_label',
    label: 'Equilibrado',
    rangeKey: 'p5.activity_medio_range',
    range: '30–90 min/día',
    imageKey: 'p5.image.medio',
    imageDefault: '/images/funnel/activity-medio.png',
  },
  {
    key: ActivityLevel.ALTO,
    labelKey: 'p5.activity_alto_label',
    label: 'Muy activo',
    rangeKey: 'p5.activity_alto_range',
    range: '> 90 min/día',
    imageKey: 'p5.image.alto',
    imageDefault: '/images/funnel/activity-alto.png',
  },
];

function resolveInitialSelection(saved: ActivityLevel | undefined, chosen: boolean | undefined): ActivityLevel | null {
  if (chosen && saved) return saved;
  if (saved && saved !== ActivityLevel.MEDIO) return saved;
  return null;
}

function P5Actividad(): React.JSX.Element {
  const { formData, updateFormData, goToNextStep, goToPreviousStep } = useForm();
  const { t, config } = useAppConfig();

  const nombre = formData.nombre || 'tu perro';

  const translations = config.funnel_config.translations;
  const activityImages: Record<ActivityLevel, string> = {
    [ActivityLevel.BAJO]: translations['p5.image.bajo'] || '/images/funnel/activity-bajo.png',
    [ActivityLevel.MEDIO]: translations['p5.image.medio'] || '/images/funnel/activity-medio.png',
    [ActivityLevel.ALTO]: translations['p5.image.alto'] || '/images/funnel/activity-alto.png',
  };

  const [selected, setSelected] = useState<ActivityLevel | null>(() =>
    resolveInitialSelection(formData.activityLevel, formData.activityLevelChosen),
  );

  useEffect(() => {
    setSelected((prev) => {
      const next = resolveInitialSelection(formData.activityLevel, formData.activityLevelChosen);
      return prev === next ? prev : next;
    });
  }, [formData.activityLevel, formData.activityLevelChosen]);

  const [attempted, setAttempted] = useState(false);

  const canContinue = selected !== null;

  const whyBullets =
    selected === ActivityLevel.BAJO
      ? [
          t('p5.why_bajo_b1', 'Ración un 5-10% menor: gasto basal reducido', { nombre }),
          t('p5.why_bajo_b2', 'Proteína de calidad para preservar masa muscular'),
        ]
      : selected === ActivityLevel.MEDIO
        ? [
            t('p5.why_medio_b1', 'Kcal estándar: equilibrio actividad/descanso', { nombre }),
            t('p5.why_medio_b2', 'Mezcla óptima de proteína magra y grasas saludables'),
          ]
        : selected === ActivityLevel.ALTO
          ? [
              t('p5.why_alto_b1', 'Ración un 15-25% mayor por alto gasto energético', { nombre }),
              t('p5.why_alto_b2', 'Más calorías de grasas buenas para resistencia'),
            ]
          : null;

  const whyCallout = whyBullets ? (
    <SoWhatCallout bullets={whyBullets} />
  ) : (
    <SoWhatCallout>
      {t('p5.why', 'Selecciona un nivel — el dato cambia las calorías que necesita {nombre} al día.', { nombre })}
    </SoWhatCallout>
  );

  const missingReason = !canContinue ? t('p5.missing_activity', 'Selecciona el nivel de actividad') : undefined;

  function handleSelect(level: ActivityLevel): void {
    setSelected(level);
    updateFormData({ activityLevel: level, activityLevelChosen: true });
  }

  return (
    <FunnelLayout
      step={5}
      useTwoPhase
      onContinue={goToNextStep}
      continueDisabled={!canContinue}
      continueDisabledReason={missingReason}
      onContinueDisabledAttempt={() => setAttempted(true)}
      onBack={goToPreviousStep}
      sidebar={whyCallout}
    >
      <div className="flex flex-col gap-5">
        <h1 className="font-fredoka text-xl sm:text-2xl font-semibold text-center text-black">
          <span className="mr-2" aria-hidden="true">
            🏃
          </span>
          {t('p5.title', '¿Cuánto ejercicio hace {nombre}?', { nombre })}
        </h1>

        <div
          className={`relative rounded-[24px] bg-gradient-to-b from-[#90D1E8]/10 to-[#FAF7F1]/60 p-3 transition-all ${
            attempted && !canContinue ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white' : ''
          }`}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            {ACTIVITY_OPTIONS.map((opt) => {
              const isActive = selected === opt.key;
              const imgSrc = activityImages[opt.key];
              return (
                <button
                  key={opt.key}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => handleSelect(opt.key)}
                  className={`relative flex flex-row sm:flex-col items-center justify-start sm:justify-start gap-3 sm:gap-0 px-3 sm:px-2 py-3 sm:pt-4 sm:pb-4 rounded-[20px] transition-all duration-200 h-24 sm:h-48 text-left sm:text-center ${
                    isActive
                      ? 'bg-white border-[2.5px] border-[#008BBC] shadow-[0_0_0_4px_rgba(0,139,188,0.15)] scale-[1.03]'
                      : 'bg-white/70 border-2 border-transparent hover:border-[#90D1E8] hover:scale-105 hover:shadow-md'
                  }`}
                >
                  {isActive && (
                    <div
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#008BBC] flex items-center justify-center shadow z-20"
                      aria-hidden="true"
                    >
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                  )}

                  <img
                    src={t(opt.imageKey, imgSrc)}
                    alt={t(opt.labelKey, opt.label)}
                    className="w-16 h-16 sm:w-24 sm:h-24 object-contain sm:mb-2 flex-shrink-0"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />

                  <span className="min-w-0 flex flex-col items-start sm:items-center justify-center sm:min-h-[2.5em]">
                    <span
                      className={`font-fredoka text-base sm:text-lg font-semibold leading-tight text-left sm:text-center ${
                        isActive ? 'text-[#008BBC]' : 'text-black/80'
                      }`}
                    >
                      {t(opt.labelKey, opt.label)}
                    </span>

                    <span
                      className={`font-poppins text-[11px] sm:text-xs mt-0.5 sm:mt-1 text-left sm:text-center leading-tight ${
                        isActive ? 'text-[#008BBC]/70' : 'text-black/45'
                      }`}
                    >
                      {t(opt.rangeKey, opt.range)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:hidden">{whyCallout}</div>
      </div>
    </FunnelLayout>
  );
}

export default P5Actividad;
